import React, { useMemo, ReactElement, ReactNode, isValidElement } from "react"
import { Anchor } from "../app/components/anchor"
import parse from "html-react-parser"

// Define valid target types to match what Anchor component expects
type AnchorTarget = "_blank" | "_self" | undefined

// Cache for rendered HTML to improve performance
const htmlCache = new Map<string, ReactElement | null>()
const MAX_CACHE_SIZE = 100 // Limit cache size to prevent memory issues

// Filter only specific dangerous attributes if needed
const DANGEROUS_ATTRIBUTES = new Set([
  // Leave empty to allow all attributes by default
  // Add specific attributes to filter if needed
])

// Dangerous URL protocols to filter - can be disabled
const DANGEROUS_PROTOCOLS = new Set(["javascript:", "data:", "vbscript:"])

/**
 * Sanitizes a URL to prevent script injection
 * This can be disabled by passing allowDangerousProtocols=true in the options
 */
const sanitizeUrl = (
  url: string,
  allowDangerousProtocols: boolean = false,
): string => {
  if (!url) return "#"

  // Skip protocol checking if dangerous protocols are allowed
  if (allowDangerousProtocols) return url

  // Check for dangerous protocols
  const lower = url.toLowerCase().trim()
  // Use Array.from to convert Set to array for older JS environments
  for (const protocol of Array.from(DANGEROUS_PROTOCOLS)) {
    if (lower.startsWith(protocol)) {
      console.warn(`Blocked dangerous URL: ${url}`)
      return "#"
    }
  }

  return url
}

/**
 * Sanitizes HTML content for security
 * This can be completely disabled by passing disableSanitization=true in the options
 */
const sanitizeHtml = (
  html: unknown,
  disableSanitization: boolean = false,
): string => {
  // Make sure we have a string
  if (html === null || html === undefined) {
    return ""
  }

  // Convert to string if it's not already
  const htmlStr = typeof html === "string" ? html : String(html)

  // Skip sanitization if disabled
  if (disableSanitization) {
    return htmlStr
  }

  // Simple sanitation to prevent obvious XSS
  // This is now optional and can be completely disabled
  return htmlStr
    .replace(/on\w+="[^"]*"/gi, "") // Remove event handlers
    .replace(/on\w+='[^']*'/gi, "")
}

/**
 * Sanitizes React children to ensure only valid React elements or primitive types are passed to React
 *
 * @param children - The children to sanitize
 * @returns - Sanitized children safe for React rendering
 */
const sanitizeReactChildren = (children: any): ReactNode => {
  // Handle null, undefined, or primitive types (string, number, boolean)
  if (
    children === null ||
    children === undefined ||
    typeof children === "string" ||
    typeof children === "number" ||
    typeof children === "boolean"
  ) {
    return children
  }

  // Handle valid React elements
  if (isValidElement(children)) {
    return children
  }

  // Handle arrays recursively
  if (Array.isArray(children)) {
    return children.map((child) => sanitizeReactChildren(child))
  }

  // Handle objects with type and content properties (coming from HTML parser)
  if (typeof children === "object" && children.type && children.content) {
    try {
      // For HTML content, return it as a string
      if (children.type === "html" && typeof children.content === "string") {
        return children.content
      }

      // For other object types, recursively sanitize their content
      return sanitizeReactChildren(children.content)
    } catch (err) {
      console.warn("Error sanitizing React children:", err)
      // If we can't process it, convert to string or return empty string
      return String(children.content || "")
    }
  }

  // For any other object, convert to string
  try {
    return String(children)
  } catch (err) {
    console.warn("Error converting object to string:", err)
    return ""
  }
}

/**
 * Safely renders HTML content, converting <a> and <Anchor> tags to React Anchor components.
 * This utility avoids any extra wrapper elements and preserves the original HTML structure.
 *
 * @param html - HTML string to render
 * @param options - Additional options for rendering
 * @returns - React element with dangerouslySetInnerHTML or null if input is empty
 */
export const safelyRenderHtml = (
  html: unknown,
  options: {
    disableCache?: boolean
    disableSanitization?: boolean
    allowDangerousProtocols?: boolean
    filterAttributes?: string[]
  } = {},
): ReactElement | null => {
  // Handle null, undefined, or empty strings
  if (html === null || html === undefined || html === "") return null

  // Ensure we're working with a string
  const htmlString = typeof html === "string" ? html : String(html)

  // Sanitize the input HTML (unless disabled)
  const sanitizedHtml = sanitizeHtml(htmlString, options.disableSanitization)

  // Empty after sanitization
  if (!sanitizedHtml) return null

  // Check cache unless disabled
  if (!options.disableCache) {
    const cached = htmlCache.get(sanitizedHtml)
    if (cached) {
      return cached
    }
  }

  // Create a set of filtered attributes if specified
  const filterAttributes = options.filterAttributes
    ? new Set(options.filterAttributes)
    : DANGEROUS_ATTRIBUTES

  try {
    // If we don't have any anchor tags, render the HTML directly
    if (!/<(a|Anchor)[\s>]/i.test(sanitizedHtml)) {
      const result = <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />

      // Cache the result unless caching is disabled
      if (!options.disableCache) {
        cacheResult(sanitizedHtml, result)
      }

      return result
    }

    // For server-side rendering, just render the HTML directly to avoid hydration issues
    if (typeof window === "undefined") {
      // Convert any Anchor tags to regular a tags for SSR
      const processedHtml = sanitizedHtml
        .replace(/<(Anchor|anchor)(\s+[^>]*)(\/?)>/gi, "<a$2$3>")
        .replace(/<\/(Anchor|anchor)>/gi, "</a>")

      const result = <div dangerouslySetInnerHTML={{ __html: processedHtml }} />

      // Cache the result unless caching is disabled
      if (!options.disableCache) {
        cacheResult(sanitizedHtml, result)
      }

      return result
    }

    // Use html-react-parser to transform HTML into React elements (client-side only)
    const parserOptions = {
      replace: (domNode: any) => {
        // Skip non-element nodes
        if (domNode.type !== "tag") return

        // Filter specific dangerous attributes if configured
        if (filterAttributes.size > 0 && domNode.attribs) {
          Object.keys(domNode.attribs).forEach((attr) => {
            if (filterAttributes.has(attr.toLowerCase())) {
              console.warn(
                `Removed filtered attribute: ${attr} from ${domNode.name}`,
              )
              delete domNode.attribs[attr]
            }
          })
        }

        // Process <a> and <Anchor> tags
        if (domNode.name && /^(a|anchor)$/i.test(domNode.name)) {
          try {
            // Get anchor attributes and sanitize the href (unless disabled)
            const rawHref = domNode.attribs?.href || "#"
            const href = sanitizeUrl(rawHref, options.allowDangerousProtocols)

            // Skip invalid hrefs
            if (href === "#" || href === "undefined") return

            // Prepare children - if it's text content or simple HTML, use dangerouslySetInnerHTML
            let childContent
            let useInnerHTML = false

            if (domNode.children && domNode.children.length > 0) {
              // If there are no nested anchors, use innerHTML approach for simplicity
              // This helps maintain formatting and prevents breaking up text
              const hasNestedAnchors = domNode.children.some((child: any) => {
                // Check for direct anchors
                if (child.type === "tag" && /^(a|anchor)$/i.test(child.name)) {
                  return true
                }
                // Check for deeply nested anchors
                if (child.children && child.children.length > 0) {
                  return child.children.some(
                    (grandchild: any) =>
                      grandchild.type === "tag" &&
                      /^(a|anchor)$/i.test(grandchild.name),
                  )
                }
                return false
              })

              if (!hasNestedAnchors) {
                // For simple content with no nested anchors, use innerHTML
                useInnerHTML = true

                // Build the original HTML content
                // This preserves all HTML formatting exactly as it was
                if (domNode.children) {
                  try {
                    // Use a temporary container to get the inner HTML
                    const tempContainer = document.createElement("div")
                    domNode.children.forEach((child: any) => {
                      if (child.type === "text") {
                        tempContainer.appendChild(
                          document.createTextNode(child.data),
                        )
                      } else if (child.type === "tag") {
                        // We're allowing all tags now
                        const elem = document.createElement(child.name)
                        // Add attributes
                        if (child.attribs) {
                          Object.entries(child.attribs).forEach(
                            ([key, value]) => {
                              // Skip filtered attributes if configured
                              if (
                                filterAttributes.size > 0 &&
                                filterAttributes.has(key.toLowerCase())
                              ) {
                                return
                              }

                              // Sanitize attributes like href (unless disabled)
                              if (key.toLowerCase() === "href") {
                                elem.setAttribute(
                                  key,
                                  sanitizeUrl(
                                    value as string,
                                    options.allowDangerousProtocols,
                                  ),
                                )
                              } else {
                                elem.setAttribute(key, value as string)
                              }
                            },
                          )
                        }
                        // Add text content
                        if (
                          child.children &&
                          child.children[0] &&
                          child.children[0].data
                        ) {
                          elem.textContent = child.children[0].data
                        }
                        tempContainer.appendChild(elem)
                      }
                    })
                    childContent = tempContainer.innerHTML
                  } catch (innerHtmlError) {
                    console.warn(
                      "Error creating inner HTML, using fallback method",
                      innerHtmlError,
                    )
                    // Fallback to simpler approach if DOM manipulation fails
                    childContent = domNode.children
                      .map((child: any) => {
                        if (child.type === "text") return child.data || ""

                        if (child.type === "tag") {
                          // Filter attributes if configured
                          const attrs = Object.entries(child.attribs || {})
                            .filter(
                              ([key]) =>
                                !(
                                  filterAttributes.size > 0 &&
                                  filterAttributes.has(key.toLowerCase())
                                ),
                            )
                            .map(([key, value]) => {
                              // Sanitize URL attributes (unless disabled)
                              if (key.toLowerCase() === "href") {
                                return `${key}="${sanitizeUrl(
                                  value as string,
                                  options.allowDangerousProtocols,
                                )}"`
                              }
                              return `${key}="${value}"`
                            })
                            .join(" ")

                          const content = child.children?.[0]?.data || ""
                          return `<${child.name} ${attrs}>${content}</${child.name}>`
                        }
                        return ""
                      })
                      .join("")
                  }
                }
              } else {
                // For complex content with nested anchors, process children recursively
                childContent = domNode.children
              }
            }

            // Extract Anchor props from attributes
            const className =
              domNode.attribs?.class || domNode.attribs?.className
            const target = domNode.attribs?.target as AnchorTarget
            const rel = domNode.attribs?.rel
            const newTab = target === "_blank" || undefined
            const dofollow = domNode.attribs?.dofollow === "true" || undefined
            const sponsored = domNode.attribs?.sponsored === "true" || undefined

            // Create the Anchor component with all needed props - use different approaches for innerHTML vs children
            if (useInnerHTML) {
              return (
                <Anchor
                  href={href}
                  className={className}
                  target={target}
                  rel={rel}
                  newTab={newTab}
                  dofollow={dofollow}
                  sponsored={sponsored}
                  dangerouslySetInnerHTML={{ __html: childContent }}
                />
              )
            } else {
              return (
                <Anchor
                  href={href}
                  className={className}
                  target={target}
                  rel={rel}
                  newTab={newTab}
                  dofollow={dofollow}
                  sponsored={sponsored}
                >
                  {childContent}
                </Anchor>
              )
            }
          } catch (anchorError) {
            console.error("Error processing anchor tag:", anchorError)
            // If anchor processing fails, leave it as is
            return
          }
        }

        // Add accessibility improvements for specific tags
        if (domNode.name.toLowerCase() === "img" && !domNode.attribs?.alt) {
          // Add empty alt for decorative images or extract alt from filename
          const src = domNode.attribs?.src || ""
          const fileName = src.split("/").pop()?.split("?")[0] || ""
          const fallbackAlt = fileName
            .replace(/[_-]/g, " ")
            .replace(/\.\w+$/, "")

          domNode.attribs.alt = fallbackAlt || ""
        }
      },
    }

    try {
      // Parse the HTML into React components
      const parsedContent = parse(sanitizedHtml, parserOptions)

      // The parser might return a string, array, or single element
      let result: ReactElement | null = null

      if (typeof parsedContent === "string") {
        result = <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
      } else if (Array.isArray(parsedContent)) {
        result = <>{parsedContent}</>
      } else {
        result = parsedContent as ReactElement
      }

      // Cache the result unless caching is disabled
      if (!options.disableCache) {
        cacheResult(sanitizedHtml, result)
      }

      return result
    } catch (parseError) {
      console.error("Error parsing HTML:", parseError)

      // Fallback to direct HTML
      const fallbackResult = (
        <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      )

      // Don't cache errors
      return fallbackResult
    }
  } catch (error) {
    console.error("Error in safelyRenderHtml:", error)

    // Ultimate fallback
    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  }
}

/**
 * Helper function to cache rendered results
 */
function cacheResult(key: string, value: ReactElement | null): void {
  // Prevent cache from growing too large
  if (htmlCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (first one added)
    const firstKey = htmlCache.keys().next().value
    if (firstKey !== undefined) {
      htmlCache.delete(firstKey)
    }
  }

  htmlCache.set(key, value)
}

/**
 * React hook that memoizes the result of safelyRenderHtml for better performance
 * Only re-renders HTML when content changes
 */
export function useSafelyRenderedHtml(html: unknown, options = {}) {
  return useMemo(() => safelyRenderHtml(html, options), [
    html,
    JSON.stringify(options),
  ])
}

// For backward compatibility
export const parseHtmlWithAnchors = safelyRenderHtml
